<template>
    <div
        :style="tagStyles"
        :class="[{'redesign-tag--animated': animated, 'bg-gray-100' : !outline }, paddingClasses]"
        class="redesign-tag text-xs flex items-center"
    >
        <slot name="extra-content">
        </slot>
        <div
            v-for="(content, index) in tagContent"
            :key="index"
            :class="{'gap-1': !index}"
            class="redesign-tag__content-item flex items-center"
        >
            <redesign-base-icon
                v-if="content.iconName"
                class="redesign-tag__icon"
                :name="content.iconName"
                :size="iconSize"
                :fill="'transparent'"
            />
            <div
                v-if="content.text"
                class="redesign-tag__text text-center"
                :class="{'uppercase': uppercase, 'font-semibold' : bold}"
                :style="`font-variant-numeric: tabular-nums; font-size: ${textSize}`"
            >
                {{ content.text }}
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { computed } from 'vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import { ITagContent } from '@/types/tag'

const hexOpacity = '19'

interface IProps {
    color?: string
    paddingClasses?: string
    borderColor?: string
    outline?: boolean
    animated?: boolean
    iconSize?: number
    tagContent?: Array<ITagContent>
    backgroundColor?: string
    uppercase?: boolean
    textSize?: string
    bold?: boolean
}

const props = withDefaults(defineProps<IProps>(), {
    color: '',
    animated: false,
    outline: false,
    paddingClasses: 'p-1',
    borderColor: '',
    backgroundColor: '',
    uppercase: true,
    iconSize: 16,
    textSize: '12px',
    bold: false,
    tagContent: () => []
})

const tagStyles = computed(() => {
    const styles: Record<string, string> = {}
    if (props.color) {
        styles.color = props.color
    }
    if (props.color && !props.outline) {
        styles['--dynamicBackgroundColor'] = props.color + hexOpacity
        styles.backgroundColor = 'var(--dynamicBackgroundColor)'
        styles.borderColor = 'transparent'

    }
    if (props.backgroundColor) {
        styles['--dynamicBackgroundColor'] = props.backgroundColor
    }
    if (props.outline && !props.borderColor) {
        styles.borderColor = props.color + hexOpacity
    }
    if (props.borderColor) {
        styles.borderColor = props.borderColor
    }
    return styles
})

</script>

<style scoped lang="scss">
.redesign-tag {
    @apply w-max relative;
    text-wrap: nowrap;
    border-color: var(--gray-200);
    border-width: 1px;
    color: var(--mainTextColor-color);
    background-color: var(--dynamicBackgroundColor);
    min-width: 10px;
    border-radius: 1rem;

    &__text {
        line-height: normal;
        align-self: center;
    }

    &--animated {
        animation: BlinkerBackground 2s infinite;

        .redesign-tag__icon {
            animation: BlinkerIcon 2s infinite;
        }
    }


    &__content-item {
        @apply relative;
        opacity: 1;

        &:not(:last-child) {
            margin-right: 13px;
        }

        &:not(:last-child)::after {
            @apply absolute top-1/2;
            content: '';
            right: -7px;
            width: 2px;
            height: 2px;
            border-radius: 50%;
            background: var(--mainTextColor-color);
        }
    }
}

@keyframes BlinkerBackground {
    0% {
        background-color: var(--dynamicBackgroundColor);
    }
    50% {
        background-color: transparent;
    }
    100% {
        background-color: var(--dynamicBackgroundColor);
    }
}

@keyframes BlinkerIcon {
    0% {
        opacity: 1;
    }
    50% {
        opacity: 0.3;
    }
    100% {
        opacity: 1;
    }
}
</style>
